import { AGENCY_TIME_ZONE, agencyCalendarDaysBetween } from '../time/agency-dates.js';
import { acceptedInCandidacy, offerAcceptedAt, offerInCandidacy } from './model.js';
/**
 * Where one candidate's offer stands, for the Offers list (#259, frame 06b `293:444`).
 *
 * DERIVED, NEVER STORED. Nothing here is a new field or a second definition of "current": the
 * group is read from the same `OfferRecord.status` the person page reads, and "signed" is
 * `acceptedOffer` — the one function ADR-011 §4 gives for that question, so the list can never
 * disagree with the per-person view about whether someone may move to Onboarding.
 *
 * The frame's five groups come first, in its order. `to-prepare` is the absence of an offer, so it
 * is a group this module can return without any record at all.
 *
 * `expired` IS A SIXTH, APPENDED (#301). It is not a new state of the world — the portal already
 * refuses a signature after the Accept-by date (`checkPdfAcceptance`: "This offer has expired") —
 * it is the Offers list catching up with what the portal has always enforced. An expired offer used
 * to sit in `out-for-signature` for ever, because the only two exits were acceptance and an explicit
 * `withdraw-offer`, so the screen said an offer was awaiting a signature that could no longer be
 * given. Appended rather than inserted so the frame's five keep their order and their positions in
 * the filter chips; `signed` and `expired` are then the two terminal groups, side by side.
 */
export const offerGroups = ['to-prepare', 'drafts', 'ready-to-send', 'out-for-signature', 'signed', 'expired'];
/**
 * How many days off the acceptance deadline is, counted the way a person reads a date off a
 * calendar: "Accept by Sep 17" seen on Sep 14 is three days, whatever hour either falls at.
 *
 * 0 means the deadline is today. Past deadlines are negative. The counting itself is
 * `agencyCalendarDaysBetween`, shared with the correction due dates (#265) so two screens cannot
 * disagree about how many days apart two dates are.
 */
export function daysUntilDeadline(deadline, at, timeZone = AGENCY_TIME_ZONE) {
    return agencyCalendarDaysBetween(at, deadline, timeZone);
}
const isOfferKind = (record) => record.kind === 'offer' || record.kind === 'existing-offer';
/**
 * Is this record an offer the candidate can still SIGN — issued, and not yet accepted?
 *
 * THE ONE DEFINITION of "out for signature", so the Offers list's live count and the closure a
 * withdrawal performs (#298) cannot disagree about the same offer.
 *
 * `status === 'issued'` AND no acceptance. A portal signature never changes the record's status —
 * `offerAcceptedAt` requires `status === 'issued'` and reads the acceptance off the approved step —
 * so an offer that has just been signed still reads as issued, and without the second clause every
 * signed offer would count as one still awaiting a signature.
 *
 * NO CANDIDACY ARGUMENT, deliberately. Whether the link in a candidate's portal still works has
 * nothing to do with which candidacy the offer was drafted in: an offer left over from an earlier
 * candidacy is exactly as signable as a fresh one. Callers that are REPORTING rather than closing
 * add the Q7 scope themselves — see `offerStandings`, which counts only in-candidacy offers because
 * an out-of-candidacy one is not what this screen is about.
 *
 * Returns a BOOLEAN, so a call site must use it directly. Comparing it against null is always true.
 */
export function offerOutForSignature(record, steps) {
    return record.kind === 'offer' && record.status === 'issued' && offerAcceptedAt(record, steps) === null;
}
/**
 * Has this offer's Accept-by date gone by (#301)?
 *
 * INSTANTS, and the SAME comparison the portal makes. `checkPdfAcceptance` refuses a submission when
 * `Date.parse(now) > Date.parse(submissionDeadline)` — the offer step carries the acceptance deadline
 * as exactly that field — so an expired offer is already unsignable in fact. This is the Offers list
 * reading the same rule rather than a second opinion about it, which is why the strictness matches:
 * an offer due at 5:00 PM can still be signed at 5:00 PM sharp, and cannot at 5:00:01.
 *
 * NOT `daysUntilDeadline`. That counts agency calendar days for a screen that says "3 days left";
 * whether one moment has passed another is a different question, and rounding it to days would call
 * an offer live all evening after its 5 PM deadline.
 *
 * Only an `offer` record has a deadline, so anything else answers false: a recorded paper offer is
 * signed or it is nothing. Returns a BOOLEAN — a call site must use it directly.
 */
export function offerExpired(record, at) {
    return record.kind === 'offer' && Date.parse(at) > Date.parse(record.terms.acceptanceDeadline);
}
/**
 * Whether an in-flight offer belongs to the candidacy running now.
 *
 * `acceptedOffer` answers this for a signed one, by the moment it was accepted. A draft, a
 * reviewed offer or one still out for signature has no such moment, so the test is when it was
 * last worked on: `createdAt` is carried through edit, review and issue (see `offerInCandidacy`),
 * so an offer drafted before a withdrawal and issued after the person came back would otherwise
 * be judged by the old candidacy and drop off this list for the very candidate it is out to.
 * An offer untouched since an earlier candidacy stays in that person's history and stops
 * counting here, which is what puts a returning candidate back under "To prepare".
 */
const inFlightInCandidacy = (record, candidacySince) => offerInCandidacy(record.updatedAt, candidacySince);
/**
 * How far along a group is, for ordering only — never a ranking the screen shows.
 *
 * It is the precedence the list used while it reported ONE row per person (`signed` outranks
 * `out-for-signature` outranks `ready-to-send` outranks `drafts`), kept because it is still the right
 * order to read one person's offers in.
 *
 * NO PRODUCTION CALLER READS `[0]`. The web groups rows by `row.group` and the API re-sorts them by
 * deadline, so this order is what a reader sees WITHIN a group and nothing more. What depends on it is
 * `tests/offers-overview.test.ts`, whose `standing()` helper reads `[0]` so that every test written
 * against the old single-row function still asserts the case it names — a test-suite convenience, not
 * an API contract, and said plainly here because an earlier version of this comment implied otherwise.
 */
const groupRank = {
    // `expired` sits BELOW `drafts`: if a candidate has a lapsed offer and a fresh draft, the draft is
    // the work in hand and the lapsed one is history. `to-prepare` is last and is only ever alone.
    signed: 0, 'out-for-signature': 1, 'ready-to-send': 2, drafts: 3, expired: 4, 'to-prepare': 5,
};
/**
 * EVERY row this person contributes to the Offers list — ONE PER OFFER, not one per person (#297).
 *
 * THE IDENTITY OF A ROW IS THE OFFER, and that is the whole change. While this returned a single
 * standing, a person with more than one live offer was reported in exactly one group: the branch
 * order picked the furthest along and the rest were silent. A reviewed replacement drafted beside
 * an issued offer landed in no group at all, so the filter chip read "Ready to send (0)" and the
 * section said "Nobody is here right now" while that offer genuinely awaited sending — and
 * `offerGroupCounts` counted PEOPLE, so no count could ever say otherwise.
 *
 * The state is reachable, not theoretical: `draft-offer` carries no guard (`hr-workflow.ts`) while
 * `issue-offer` refuses a second ISSUED offer, so any number of drafts and reviewed offers coexist
 * with one that is out.
 *
 * Each row's group is read from its OWN record, so nothing is hidden behind a sibling. A withdrawn
 * offer is not a group — the frame's footer says so — and contributes no row at all; nor does one
 * left over from an earlier candidacy.
 *
 * `to-prepare` IS THE ABSENCE OF AN OFFER, so it is returned only when this person contributes no
 * offer row whatsoever, and then exactly once. A person with a signed offer is not also someone to
 * prepare an offer for.
 *
 * ALWAYS AT LEAST ONE ROW, so a candidate can never drop off the screen: the caller may flatten
 * this straight into the list. Ordered furthest-along first, with the old tie-breaks inside each
 * group (soonest deadline among those out for signature, most recent acceptance among signed,
 * latest write among drafts and reviewed offers) — see `groupRank` for what does and does not
 * depend on that order.
 */
export function offerStandings(person, at) {
    const { candidacySince } = person;
    const offers = person.records.filter(isOfferKind);
    // Counted before any group is chosen, because it matters most in the group that is NOT
    // out-for-signature: a recorded paper signature does not withdraw a live portal offer.
    //
    // `offerOutForSignature` is the shared definition, so this count and the closure a withdrawal
    // performs (#298) cannot disagree about which offers are still awaiting a signature. The Q7
    // candidacy scope is added HERE rather than inside it: an offer left over from an earlier
    // candidacy is just as signable, but it is not what this screen reports on.
    //
    // PERSON-WIDE, and it stays that way now every offer has a row of its own: it is what lets a
    // SIGNED row warn that another offer is still in the candidate's portal (#275), which is a fact
    // about the person rather than about the row it is printed on.
    //
    // AN EXPIRED OFFER IS NOT STILL OUT (#301). It kept incrementing this, so a signed row warned that
    // "an offer is still out for signature" about one the portal would refuse, and an out-for-signature
    // row said "one of 2 offers out" when only one could be signed. The portal already enforces the
    // deadline; this is the count agreeing with it.
    const stillOut = offers.filter((record) => offerOutForSignature(record, person.steps) && !offerExpired(record, at)
        && inFlightInCandidacy(record, candidacySince)).length;
    const rows = [];
    for (const record of offers) {
        const acceptedAt = offerAcceptedAt(record, person.steps);
        if (acceptedAt !== null) {
            // `acceptedInCandidacy`, not `offerInCandidacy`: it is the same function the onboarding gate
            // uses, so this list cannot call an offer out-of-candidacy that the gate accepts.
            if (!acceptedInCandidacy(record, acceptedAt, candidacySince))
                continue;
            rows.push({
                group: 'signed', record, signedAt: acceptedAt, dueInDays: null, liveOffers: stillOut,
                // FROM THIS RECORD, so a candidate who signed in the portal AND on paper has both named, one
                // per row. `existing-offer` is the paper path by definition; an `offer` was signed in the
                // portal, because that is the only way its step reaches `approved`.
                signedVia: record.kind === 'existing-offer' ? 'recorded' : 'portal',
            });
            continue;
        }
        // Only `offer` records have a status; an `existing-offer` is signed or it is nothing, and an
        // unaccepted one has just been refused above.
        if (record.kind !== 'offer')
            continue;
        if (record.status === 'withdrawn' || !inFlightInCandidacy(record, candidacySince))
            continue;
        const rest = { record, signedAt: null, liveOffers: stillOut, signedVia: null };
        if (record.status === 'issued') {
            // TERMINAL ONCE THE DEADLINE HAS GONE BY (#301). The offer is still in the candidate's portal
            // and the portal will refuse to take a signature for it, so it is not awaiting one: it keeps a
            // row, because an offer that lapsed is something HR has to act on rather than something to
            // hide, and it carries no countdown, because there is nothing left to count.
            rows.push(offerExpired(record, at)
                ? { ...rest, group: 'expired', dueInDays: null }
                : { ...rest, group: 'out-for-signature', dueInDays: daysUntilDeadline(record.terms.acceptanceDeadline, at) });
        }
        else if (record.status === 'reviewed') {
            rows.push({ ...rest, group: 'ready-to-send', dueInDays: null });
        }
        else {
            rows.push({ ...rest, group: 'drafts', dueInDays: null });
        }
    }
    if (rows.length === 0)
        return [{ group: 'to-prepare', record: null, signedAt: null, dueInDays: null, liveOffers: stillOut, signedVia: null }];
    return [...rows].sort((a, b) => groupRank[a.group] - groupRank[b.group] || withinGroup(a, b));
}
/**
 * Whether this person has an offer on the record that counts in the candidacy running now.
 *
 * A DERIVATION OVER `offerStandings`, not a second definition of "an offer exists": `to-prepare`
 * is by construction the absence of an offer row, so this is exactly "some standing is not
 * `to-prepare`". That is what keeps a withdrawn offer, and one left over from an earlier
 * candidacy (ADR-011 Q7), from reading as an offer in progress. An expired offer still counts: it
 * has a row of its own because it is something HR has to act on.
 */
export function hasOfferInCandidacy(person, at) {
    return offerStandings(person, at).some((standing) => standing.group !== 'to-prepare');
}
/**
 * The tie-break inside one group, so two readings of the same records put the rows in the
 * same order.
 *
 * THE SOONEST DEADLINE FIRST among those out for signature — the one about to run out, not the one
 * written last. The most recent ACCEPTANCE first among signed, by the acceptance itself rather than
 * by the record's last write: an issued offer's `updatedAt` is frozen at the issue instant. The
 * latest write first everywhere else, which is all a draft has.
 */
function withinGroup(a, b) {
    if (a.record?.kind === 'offer' && b.record?.kind === 'offer') {
        const deadlines = Date.parse(a.record.terms.acceptanceDeadline) - Date.parse(b.record.terms.acceptanceDeadline);
        // Soonest first while they are out; most recently lapsed first once they are expired, which is the
        // one HR was last waiting on.
        if (a.group === 'out-for-signature')
            return deadlines;
        if (a.group === 'expired')
            return -deadlines;
    }
    if (a.group === 'signed' && a.signedAt !== null && b.signedAt !== null)
        return Date.parse(b.signedAt) - Date.parse(a.signedAt);
    // 0, never NaN, when a row has no record: `to-prepare` is the only such group and it is returned
    // alone, but a comparator that can answer NaN sorts unpredictably rather than visibly.
    if (a.record === null || b.record === null)
        return 0;
    return Date.parse(b.record.updatedAt) - Date.parse(a.record.updatedAt);
}
/**
 * Every group, in the frame's order, with its count — including the groups that are empty.
 *
 * ROWS, WHICH ARE OFFERS (#297). Since `offerStandings` returns one standing per offer, these are
 * counts of offers rather than of people, and every count is the number of rows the list draws
 * under that heading — so a chip and the section beneath it cannot disagree. `to-prepare` is the
 * one group whose row is a person rather than an offer, because that group is the absence of one.
 */
export function offerGroupCounts(standings) {
    const counts = Object.fromEntries(offerGroups.map((group) => [group, 0]));
    for (const standing of standings)
        counts[standing.group] += 1;
    return counts;
}
//# sourceMappingURL=offers.js.map